import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { api, edenFetch } from "@/lib/eden";
import { useMutation } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";

interface ImpersonationInfo {
  admin_user_id: string;
  admin_name: string | null;
  expires_at: string | null;
}

/**
 * The PRD 08 D9 impersonation banner: whenever the current session is an admin signed
 * in AS a member, this renders an unmissable strip ABOVE the site, pushing the header
 * down rather than covering it (BDAY-365). Sign-in-as is impossible to do invisibly —
 * that's the point (§1 success signals).
 */
export function ImpersonationBanner() {
  const { user } = useAuth();
  const impersonation =
    (user as unknown as { impersonation?: ImpersonationInfo | null } | null)?.impersonation ?? null;

  // Exit returns to the member's admin page (BDAY-366).
  const memberId = user?.user_id;
  const exitMutation = useMutation({
    mutationFn: async () => edenFetch(api.api.user.me.impersonation.exit.post()),
    onSuccess: () => {
      window.location.href = memberId ? `/admin/users/${memberId}` : "/admin";
    },
  });

  if (!impersonation) return null;

  const expires = impersonation.expires_at
    ? new Date(impersonation.expires_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      className="sticky top-0 z-[100] flex items-center justify-center gap-3 bg-amber-500 px-4 py-1.5 text-sm font-medium text-black shadow-md"
      data-testid="impersonation-banner"
    >
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <span className="truncate">
        Support session: viewing as <strong>{user?.display_name || user?.username}</strong>
        {impersonation.admin_name ? ` (admin: ${impersonation.admin_name})` : ""}
        {expires ? ` — ends ${expires}` : ""}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-6 border-black/30 bg-transparent px-2 text-xs text-black hover:bg-black/10"
        disabled={exitMutation.isPending}
        onClick={() => exitMutation.mutate()}
      >
        {exitMutation.isPending ? "Exiting…" : "Exit"}
      </Button>
    </div>
  );
}
